import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { CartUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Issanova size-upgrade upsell (cart drawer).
 *
 * Replaces the smaller, currently-in-cart variant with the next larger
 * variant of the same fragrance (a real, separately priced "upgrade"
 * variant that already carries the correct -10% price), so the price
 * shown in the cart always matches the price charged at checkout.
 *
 * Flow: add the larger variant first, then remove the smaller line only
 * if the add succeeded — the customer is never left without a product.
 *
 * @extends {Component}
 */
class IssanovaUpgradeUpsell extends Component {
  #viewedTracked = false;

  connectedCallback() {
    super.connectedCallback();
    this.#trackViewed();
  }

  #trackViewed() {
    if (this.#viewedTracked) return;
    this.#viewedTracked = true;

    document.dispatchEvent(
      new CustomEvent('issanova:upgrade_viewed', {
        bubbles: true,
        detail: {
          currentVariantId: this.dataset.currentVariantId,
          targetVariantId: this.dataset.targetVariantId,
        },
      })
    );
  }

  /**
   * Handles a click on the "Auf X ML upgraden" button.
   * @param {MouseEvent} event
   */
  handleUpgrade = async (event) => {
    event.preventDefault();

    const button = this.refs?.upgradeButton;
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    const { currentVariantId, targetVariantId, quantity, sectionId } = this.dataset;
    if (!currentVariantId || !targetVariantId || !sectionId) return;

    const errorEl = this.refs?.upgradeError;

    button.disabled = true;
    button.classList.add('is-loading');
    errorEl?.classList.add('hidden');

    const addUrl = window.Theme?.routes?.cart_add_url || '/cart/add.js';
    const changeUrl = window.Theme?.routes?.cart_change_url || '/cart/change.js';
    const sectionsParam = [sectionId];

    try {
      // Step 1: add the larger, discounted variant. If this fails (e.g. out
      // of stock), the cart is left completely untouched.
      const addResponse = await fetch(
        addUrl,
        fetchConfig('json', {
          body: JSON.stringify({
            items: [{ id: Number(targetVariantId), quantity: Number(quantity) || 1 }],
            sections: sectionsParam.join(','),
            sections_url: window.location.pathname,
          }),
        })
      );

      const addData = await addResponse.json();

      if (!addResponse.ok || addData.status || addData.errors) {
        throw new Error(addData.description || addData.message || 'add_failed');
      }

      // Step 2: remove the smaller line now that the upgrade is confirmed in the cart.
      const changeResponse = await fetch(
        changeUrl,
        fetchConfig('json', {
          body: JSON.stringify({
            id: String(currentVariantId),
            quantity: 0,
            sections: sectionsParam.join(','),
            sections_url: window.location.pathname,
          }),
        })
      );

      const changeData = await changeResponse.json();

      const sectionsHtml = changeData.sections || addData.sections;
      if (sectionsHtml?.[sectionId]) {
        morphSection(sectionId, sectionsHtml[sectionId]);
      }

      document.dispatchEvent(
        new CartUpdateEvent(changeData, sectionId, {
          itemCount: changeData.item_count,
          source: 'issanova-upgrade-upsell',
          sections: sectionsHtml,
        })
      );

      document.dispatchEvent(
        new CustomEvent('issanova:upgrade_accepted', {
          bubbles: true,
          detail: { currentVariantId, targetVariantId },
        })
      );
    } catch (error) {
      console.error('[issanova-upgrade-upsell]', error);
      errorEl?.classList.remove('hidden');
      button.disabled = false;
      button.classList.remove('is-loading');
    }
  };
}

if (!customElements.get('issanova-upgrade-upsell')) {
  customElements.define('issanova-upgrade-upsell', IssanovaUpgradeUpsell);
}
